import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tooltip } from '@material-ui/core';

import { IconName, Illustration } from '@klp/design';
import { KlpPopover } from '@klp/design/KlpPopover/KlpPopover';
import { FileDownload } from '@klp/filedownload';

import { contractLeaseAgreementApiRoutes } from '../api';
import type { DocumentInfo, LeaseAgreement } from '../types';
import { getFileTypeIcon } from '../utils';

type ContractDownloadProps = {
    row: LeaseAgreement;
    classes: ContractDownloadClasses;
};

type ContractDownloadClasses = {
    download: string;
    previewDownload: string;
    fileDisplay: string;
    fileList: string;
};

const ContractDownloadComponent: React.FC<ContractDownloadProps> = ({
    row,
    classes,
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = React.useState(false);
    const anchorRef = React.useRef<HTMLButtonElement>(null);
    const documents = row.dokuments;
    // eslint-disable-next-line react/jsx-no-useless-fragment
    if (!documents || documents.length === 0) return <></>;

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setIsOpen(true);
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    if (documents.length === 1) {
        return (
            <div className={classes.download}>
                <Tooltip title={t('general.preview')}>
                    <FileDownload
                        path={contractLeaseAgreementApiRoutes.download(
                            documents[0].id,
                        )}
                        name={`${documents[0].name}`}
                        preview={true}
                    >
                        <Illustration
                            className={classes.previewDownload}
                            icon={IconName.SEARCH}
                            klpPalette="primary"
                            klpColor="main"
                        />
                    </FileDownload>
                </Tooltip>
                <Tooltip title={t('general.download')}>
                    <FileDownload
                        path={contractLeaseAgreementApiRoutes.download(
                            documents[0].id,
                        )}
                        name={`${documents[0].name}`}
                    >
                        <Illustration
                            className={classes.previewDownload}
                            icon={IconName.DOWNLOAD}
                            klpPalette="primary"
                            klpColor="main"
                        />
                    </FileDownload>
                </Tooltip>
            </div>
        );
    }

    return (
        <>
            <Button
                onClick={handleClick}
                aria-describedby="filesModal"
                ref={anchorRef}
                variant="text"
                size="small"
            >
                {t('kontrakt.showFiles')}
            </Button>
            <KlpPopover
                open={isOpen}
                onClose={handleClose}
                anchorEl={anchorRef.current}
            >
                <div className={classes.fileList}>
                    {documents.map((document: DocumentInfo) => (
                        <div key={document.id} className={classes.fileDisplay}>
                            <Illustration
                                icon={getFileTypeIcon(
                                    document.mimeType ?? 'unknown',
                                )}
                                klpPalette="primary"
                                klpColor="main"
                            />

                            <span>
                                {document.name ?? t('kontrakt.unknownFileName')}
                            </span>
                            <div className={classes.download}>
                                <FileDownload
                                    path={contractLeaseAgreementApiRoutes.download(
                                        document.id,
                                    )}
                                    name={`${document.name}`}
                                    preview={true}
                                >
                                    <Tooltip title={t('general.preview')}>
                                        <Illustration
                                            className={classes.previewDownload}
                                            icon={IconName.SEARCH}
                                            klpPalette="primary"
                                            klpColor="main"
                                        />
                                    </Tooltip>
                                </FileDownload>
                                <FileDownload
                                    path={contractLeaseAgreementApiRoutes.download(
                                        document.id,
                                    )}
                                    name={`${document.name}`}
                                >
                                    <Tooltip title={t('general.download')}>
                                        <Illustration
                                            className={classes.previewDownload}
                                            icon={IconName.DOWNLOAD}
                                            klpPalette="primary"
                                            klpColor="main"
                                        />
                                    </Tooltip>
                                </FileDownload>
                            </div>
                        </div>
                    ))}
                </div>
            </KlpPopover>
        </>
    );
};

export const ContractDownload = (
    row: LeaseAgreement,
    classes: ContractDownloadClasses,
): JSX.Element => {
    return <ContractDownloadComponent row={row} classes={classes} />;
};
